import { useTheme } from '@mui/material/styles';
import {
  CloseBox,
  CloseIconStyled,
  Content,
  StyledDialog,
  DividerStyled,
  TextDivider,
  ButtonDivider,
  ResendPix,
  Icon,
  BodySmallRegularStyled,
  BodyMediumRegularStyled
} from 'v3/shared/components/Modal/PixSMSConfirmationModal/styles';
import { TextContent } from 'v3/shared/components/Modal/PixSMSConfirmationModal/styles';
import { HeadlineSmall } from 'v3/shared/typography/HeadlineSmall';
import { BodySmallBold, BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { TimerContent } from 'v3/shared/components/Modal/PixSMSConfirmationModal/styles';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { BodyMediumBold, BodyMediumRegular } from 'v3/shared/typography/BodyMedium';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { IPixSMSConfirmationModalProps, PixSMSModalState } from 'v3/shared/components/Modal/PixSMSConfirmationModal/types';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { PixQRCodePaymentRequest } from 'v3/services/interfaces/request';

import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import exclamationIcon from 'assets/icons/icon-exclamation.svg';

const SECS_PER_MINUTE = 60;
const COUNTDOWN_INITIAL_TIME_PIX_CODE = 60;
const STORAGE_KEY = 'pix-sms-countdown-expires-at';

export const PixSMSConfirmationModal: React.FC<IPixSMSConfirmationModalProps> = ({ open, setOpenModal, setOpenQRCodePix }) => {
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { itemsToPay } = useStoreSelector((store) => store.installments);
  const theme = useTheme();

  const [modalState, setModalState] = useState<PixSMSModalState>(PixSMSModalState.LOADING);
  const [totalTime, setTotalTime] = useState(COUNTDOWN_INITIAL_TIME_PIX_CODE);
  const intervalRef = useRef<number | null>(null);

  const startTimer = useCallback((expiresAt: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setTotalTime(remaining);

      if (remaining <= 0) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        setModalState(PixSMSModalState.EXPIRED);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };

    tick();
    intervalRef.current = window.setInterval(tick, 1000);
    sessionStorage.setItem(STORAGE_KEY, String(expiresAt));
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    sessionStorage.removeItem(STORAGE_KEY);
    setTotalTime(COUNTDOWN_INITIAL_TIME_PIX_CODE);
    setModalState(PixSMSModalState.LOADING);
  }, [setOpenModal]);

  const fetchAndStartSMSPixConfirmation = useCallback(async () => {
    setModalState(PixSMSModalState.LOADING);

    const itemNumbers: number[] = itemsToPay.map((item) => item.number);
    const installmentsToPay: PixQRCodePaymentRequest = {
      numbersOfInstallments: itemNumbers,
    };

    try {
      await api.generatePixQRCode(contractingKey, installmentsToPay);
      const expiresAt = Date.now() + COUNTDOWN_INITIAL_TIME_PIX_CODE * 1000;
      startTimer(expiresAt);
      setModalState(PixSMSModalState.SUCCESS);
    } catch (error) {
      console.error("Erro on generate Pix SMS Confirmation:", error);
      setModalState(PixSMSModalState.ERROR);
    }
  }, [contractingKey, itemsToPay, startTimer]);


  useEffect(() => {
    if (open) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const now = Date.now();

      if (stored && Number(stored) > now) {
        startTimer(Number(stored));
        setModalState(PixSMSModalState.SUCCESS);
      } else {
        fetchAndStartSMSPixConfirmation();
      }
    } else {
      handleCloseModal();
    }
  }, [open, fetchAndStartSMSPixConfirmation, handleCloseModal, startTimer]);

  const handleResendClick = () => {
    fetchAndStartSMSPixConfirmation();
  };

  const minutes = Math.floor(totalTime / SECS_PER_MINUTE);
  const seconds = totalTime % SECS_PER_MINUTE;

  const renderModalContent = () => {
    switch (modalState) {
      case PixSMSModalState.LOADING:
        return null;

      case PixSMSModalState.ERROR:
        return (
          <>
            <Icon src={exclamationIcon} alt="Erro" />
            <BodyMediumRegularStyled>
              Infelizmente não foi possível acessar a chave Pix
            </BodyMediumRegularStyled>
            <BodySmallRegularStyled>
              Outra tentativa deverá ser realizada
            </BodySmallRegularStyled>
            <FlexButton
              text="Voltar a página inicial"
              size="small"
              handleClick={handleCloseModal}
            />
          </>
        );

      case PixSMSModalState.EXPIRED:
      case PixSMSModalState.SUCCESS:
      default:
        return (
          <>
            <HeadlineSmall color={theme.palette.common.white}>QR Code e chave Pix enviados com sucesso!</HeadlineSmall>

            <TextContent>
              <BodySmallRegular>
                Prontinho! O QR Code e a chave Pix foram enviados para o e-mail e SMS cadastrados na conta Dimo do cliente,
                e ficam ativos por 60 minutos.
              </BodySmallRegular>
              <BodySmallRegular>
                Se o cliente não receber em alguns instantes, é só aguardar um pouquinho e, se ainda assim não chegar, basta
                solicitar um novo código.
              </BodySmallRegular>
              <BodySmallRegular>
                Caso tenha dúvidas sobre os dados de contato cadastrados, o cliente pode verificar no app Dimo ou entrar em
                contato com a nossa central de atendimento.
              </BodySmallRegular>
            </TextContent>

            <TimerContent>
              <TextDivider>
                {modalState === PixSMSModalState.SUCCESS ? (
                  <Fragment>
                    <BodySmallRegular color={theme.palette.common.white}>Reenviar outro link em: </BodySmallRegular>
                    <BodyMediumBold color={theme.palette.common.white}>
                      {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                    </BodyMediumBold>
                  </Fragment>
                ) : (
                  <Fragment>
                    <BodySmallRegular color={theme.palette.common.white}>O link não chegou?</BodySmallRegular>
                    <ResendPix onClick={handleResendClick}>
                      <BodySmallBold>Reenviar link para o e-mail/ SMS</BodySmallBold>
                    </ResendPix>
                  </Fragment>
                )}
              </TextDivider>

              <DividerStyled>
                <BodySmallRegular>ou</BodySmallRegular>
              </DividerStyled>

              <ButtonDivider>
                <TextButton
                  text="Exibir QR Code"
                  size="small"
                  handleClick={setOpenQRCodePix}
                />
              </ButtonDivider>
            </TimerContent>
          </>
        );
    }
  };

  return (
    <StyledDialog
      open={open && modalState !== PixSMSModalState.LOADING}
      onClose={handleCloseModal}
    >
      <CloseBox>
        <CloseIconStyled onClick={handleCloseModal} aria-label="Fechar modal" />
      </CloseBox>

      <Content>
        {renderModalContent()}
      </Content>
    </StyledDialog>
  );
};